import Image from 'next/image';
import { IconStar } from '@tabler/icons-react';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { Title } from '~/domains/design-system/Title';
import { Text } from '~/domains/design-system/Text';

import { useStyles, MODAL_ICON_SIZE } from './PromoModalShell.styles';

export type PromoModalSection = {
  title: string;
  text: string;
};

export interface PromoModalShellProps {
  isOpen?: boolean;
  isClosing?: boolean;
  onClose?: () => void;
  icon: string;
  title: string;
  availableLabel: string;
  body: PromoModalSection[];
  note?: string;
  dataTestId?: string;
  hasSteps?: boolean;
}

/**
 * Shared presentational shell for the Contest Detail promo modals. Renders inside the promotions
 * modal stack (which supplies isOpen/isClosing/onClose). Hardcoded, informational, no CTA for V1.
 */
export function PromoModalShell({
  isOpen,
  isClosing,
  onClose,
  icon,
  title,
  availableLabel,
  body,
  note,
  dataTestId,
  hasSteps,
}: PromoModalShellProps) {
  const { classes, theme } = useStyles();

  return (
    <Dialog
      type={{ desktop: 'modal', mobile: 'drawer' }}
      isOpen={isOpen}
      isClosing={isClosing}
      onClose={onClose}
      dataTestId={dataTestId}
    >
      <DialogBody>
        <div className={classes.modalContent}>
          <div className={classes.modalIcon}>
            <Image
              src={icon}
              alt=""
              fill
              sizes={`${MODAL_ICON_SIZE}px`}
              className={classes.modalIconImage}
            />
          </div>
          <Title variant="subhead-x-large" tag="h2" c="gray2.6" className={classes.modalTitle}>
            {title}
            <Text variant="subhead-x-small" className={classes.modalTag}>
              {availableLabel}
            </Text>
          </Title>
          <div className={classes.modalBody}>
            {body.map((row, index) => (
              <div key={row.title} className={classes.modalSection}>
                <div className={classes.modalSectionBullet}>
                  {hasSteps ? (
                    <Text variant="headline-small" c="gray2.6">
                      {index + 1}
                    </Text>
                  ) : (
                    <IconStar size={14} stroke={3} color={theme.colors.gray2[4]} />
                  )}
                </div>
                <div className={classes.modalSectionContent}>
                  <Text variant="headline-medium" c="gray2.5">
                    {row.title}
                  </Text>
                  <Text variant="title-medium" c="gray2.5" className={classes.modalSectionText}>
                    {row.text}
                  </Text>
                </div>
              </div>
            ))}
          </div>
          {note && (
            <Text variant="subhead-medium" c="gray2.5" className={classes.modalNote}>
              {note}
            </Text>
          )}
        </div>
      </DialogBody>
    </Dialog>
  );
}
